import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Brain, Users, Briefcase, ShieldCheck, AlertTriangle, Heart, Award, Sparkles, Gift } from 'lucide-react';
import { activitiesAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const ICONS = { Brain, Users, Briefcase, ShieldCheck, AlertTriangle, Heart, Award, Sparkles };

// Emotional feedback messages per question
const FEEDBACK_MESSAGES = [
  "Entendo. Reconhecer é o primeiro passo da transformação.",
  "Obrigado por ser honesto consigo mesmo.",
  "Sua coragem em responder já mostra força interior.",
  "A consciência do que vivemos é a semente da mudança.",
  "Cada resposta é um passo rumo ao equilíbrio.",
  "Você não está sozinho nessa jornada.",
  "A vulnerabilidade é uma forma de coragem.",
  "Sua história importa. Continue.",
  "O autoconhecimento é a chave da Enantiodromia.",
  "Quase lá. Cada passo conta.",
];

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
};

function CircularProgress({ value, max, color }) {
  const pct = Math.round((value / max) * 100);
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="relative w-36 h-36 mx-auto" data-testid="result-circular-progress">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
        <motion.circle
          cx="60" cy="60" r={r} fill="none"
          stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white">{pct}%</span>
      </div>
    </div>
  );
}

function WelcomeScreen({ config, onStart }) {
  const Icon = ICONS[config.icon] || Brain;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center"
      data-testid="quiz-welcome-screen"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        className="w-24 h-24 rounded-full flex items-center justify-center mb-8"
        style={{ background: `linear-gradient(135deg, ${config.accentColor}33, ${config.accentColor}11)`, border: `2px solid ${config.accentColor}44` }}
      >
        <Icon className="w-12 h-12" style={{ color: config.accentColor }} />
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight"
      >
        {config.welcomeTitle}
      </motion.h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-sm sm:text-base text-gray-400 mb-2 max-w-sm"
      >
        {config.welcomeSubtitle}
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="text-xs text-gray-500 mb-10"
      >
        Leva menos de 2 minutos
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={onStart}
        className="w-full max-w-xs py-4 rounded-xl text-base font-semibold text-white flex items-center justify-center gap-2"
        style={{ background: `linear-gradient(135deg, ${config.accentColor}, ${config.accentColorAlt})` }}
        data-testid="quiz-start-btn"
      >
        Começar Agora <ArrowRight className="w-5 h-5" />
      </motion.button>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 0.9 }}
        className="text-xs text-gray-600 mt-6"
      >
        100% confidencial e gratuito
      </motion.p>
    </motion.div>
  );
}

function QuestionScreen({ question, questionIndex, totalQuestions, selectedValue, onSelect, onNext, onPrev, config, feedbackMsg }) {
  const [direction, setDirection] = useState(1);
  const [showFeedback, setShowFeedback] = useState(false);
  const pct = Math.round(((questionIndex + 1) / totalQuestions) * 100);

  const handleSelect = (qId, val) => {
    onSelect(qId, val);
    setShowFeedback(true);
    // Auto-advance after showing feedback
    setTimeout(() => {
      setShowFeedback(false);
      if (questionIndex < totalQuestions - 1) {
        setDirection(1);
        onNext();
      }
    }, 1400);
  };

  const handlePrev = () => { setDirection(-1); setShowFeedback(false); onPrev(); };

  const handleNext = () => { setDirection(1); setShowFeedback(false); onNext(); };

  return (
    <div className="flex flex-col min-h-[80vh] px-4 sm:px-6 pt-6 pb-8" data-testid="quiz-question-screen">
      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-gray-500 mb-2">
          <span>{questionIndex + 1} de {totalQuestions}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: `linear-gradient(90deg, ${config.accentColor}, ${config.accentColorAlt})` }}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      {/* Feedback overlay */}
      <AnimatePresence>
        {showFeedback && feedbackMsg && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-3 rounded-xl text-center text-sm italic"
            style={{ background: `${config.accentColor}15`, color: config.accentColorAlt, border: `1px solid ${config.accentColor}30` }}
            data-testid="quiz-feedback-msg"
          >
            {feedbackMsg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Question */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={question.id}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="flex-1 flex flex-col"
        >
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-6 leading-relaxed">
            {question.text}
          </h2>

          <div className="space-y-3 flex-1">
            {question.options.map((opt, idx) => {
              const isSelected = selectedValue === opt.value;
              return (
                <motion.button
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.08 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSelect(question.id, opt.value)}
                  disabled={showFeedback}
                  data-testid={`quiz-option-${question.id}-${idx}`}
                  className={`w-full text-left p-4 rounded-xl border transition-all duration-200 text-sm sm:text-base ${
                    isSelected
                      ? 'border-transparent text-white font-medium'
                      : 'border-white/10 text-gray-300 hover:border-white/20 hover:bg-white/5'
                  } ${showFeedback ? 'pointer-events-none' : ''}`}
                  style={isSelected ? {
                    background: `linear-gradient(135deg, ${config.accentColor}22, ${config.accentColor}11)`,
                    borderColor: config.accentColor,
                    boxShadow: `0 0 20px ${config.accentColor}15`,
                  } : {}}
                >
                  <span className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      isSelected ? 'border-transparent' : 'border-white/20'
                    }`} style={isSelected ? { borderColor: config.accentColor, background: config.accentColor } : {}}>
                      {isSelected && <span className="w-2 h-2 rounded-full bg-white" />}
                    </span>
                    {opt.label}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-6 pt-4 border-t border-white/5">
        <button
          onClick={handlePrev}
          disabled={questionIndex === 0 || showFeedback}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="quiz-prev-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Anterior
        </button>
        {questionIndex === totalQuestions - 1 && selectedValue !== undefined && (
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleNext}
            disabled={showFeedback}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all"
            style={{ background: `linear-gradient(135deg, ${config.accentColor}, ${config.accentColorAlt})` }}
            data-testid="quiz-next-btn"
          >
            Ver Resultado <ArrowRight className="w-4 h-4" />
          </motion.button>
        )}
      </div>
    </div>
  );
}

function ResultScreen({ result, totalScore, maxScore, config, onRegister, tokensAwarded }) {
  const ResultIcon = ICONS[result.iconName] || Heart;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center"
      data-testid="quiz-result-screen"
    >
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 150 }}
      >
        <CircularProgress value={totalScore} max={maxScore} color={result.neonColor} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-6 mb-2"
      >
        <span
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase"
          style={{ background: `${result.neonColor}22`, color: result.neonColor, border: `1px solid ${result.neonColor}44` }}
        >
          <ResultIcon className="w-4 h-4" />
          {result.title}
        </span>
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="text-xl sm:text-2xl font-bold text-white mt-4 mb-3"
      >
        {result.headline}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="text-sm text-gray-400 max-w-md leading-relaxed mb-4"
      >
        {result.message}
      </motion.p>

      {/* AWA Reward Banner */}
      {tokensAwarded > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.0, type: 'spring' }}
          className="flex items-center gap-2 px-5 py-3 rounded-xl mb-6"
          style={{ background: 'linear-gradient(135deg, #f59e0b22, #f59e0b11)', border: '1px solid #f59e0b44' }}
          data-testid="quiz-awa-reward"
        >
          <Gift className="w-5 h-5 text-amber-400" />
          <span className="text-amber-300 font-semibold text-sm">+{tokensAwarded} AWA</span>
          <span className="text-amber-400/60 text-xs">Bônus de Primeiro Passo!</span>
        </motion.div>
      )}

      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={onRegister}
        className="w-full max-w-xs py-4 rounded-xl text-base font-semibold text-white flex items-center justify-center gap-2"
        style={{ background: `linear-gradient(135deg, ${config.accentColor}, ${config.accentColorAlt})` }}
        data-testid="quiz-register-btn"
      >
        {config.ctaText} <ArrowRight className="w-5 h-5" />
      </motion.button>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ delay: 1.4 }}
        className="text-xs text-gray-600 mt-6 max-w-xs"
      >
        {config.disclaimer}
      </motion.p>
    </motion.div>
  );
}

export default function PremiumQuiz({ questions, config, getResult }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [phase, setPhase] = useState('welcome');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [tokensAwarded, setTokensAwarded] = useState(0);

  const totalScore = Object.values(answers).reduce((sum, v) => sum + v, 0);
  const maxScore = questions.length * 3;

  const handleSelect = useCallback((qId, val) => {
    setAnswers(prev => ({ ...prev, [qId]: val }));
  }, []);

  const handleNext = useCallback(() => {
    if (step < questions.length - 1) {
      setStep(s => s + 1);
    } else {
      // Entering result phase
      setPhase('result');
      // Try to award AWA if user is logged in
      const result = getResult(totalScore);
      const quizData = {
        role_type: config.role === 'paciente' ? 'user' : config.role === 'familiar' ? 'family' : 'professional',
        level: result.level,
        score: totalScore,
        quiz_type: config.role === 'paciente' ? 'recuperacao' : config.role === 'familiar' ? 'familiares' : 'profissionais',
      };
      // Save to localStorage for registration flow
      localStorage.setItem('uclean_quiz_role', quizData.role_type);
      localStorage.setItem('uclean_quiz_result', JSON.stringify({
        ...quizData,
        title: result.title,
        date: new Date().toISOString(),
      }));
      // If logged in, call API to award tokens
      if (user) {
        activitiesAPI.completeQuiz(quizData)
          .then(res => {
            if (res.data?.tokens_awarded > 0) {
              setTokensAwarded(res.data.tokens_awarded);
            }
          })
          .catch(() => {}); // silently fail for non-auth users
      }
    }
  }, [step, questions.length, totalScore, config.role, getResult, user]);

  const handlePrev = useCallback(() => {
    setStep(s => Math.max(0, s - 1));
  }, []);

  const handleRegister = useCallback(() => {
    if (user) {
      // Already logged in, go to feed
      navigate('/feed');
    } else {
      navigate('/login?tab=register&from=quiz');
    }
  }, [navigate, user]);

  const feedbackMsg = FEEDBACK_MESSAGES[step % FEEDBACK_MESSAGES.length];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #06061a 0%, #0c0c2d 50%, #0a0a1a 100%)' }}>
      <div className="max-w-lg mx-auto">
        <AnimatePresence mode="wait">
          {phase === 'welcome' && (
            <WelcomeScreen key="welcome" config={config} onStart={() => setPhase('questions')} />
          )}
          {phase === 'questions' && (
            <QuestionScreen
              key={`q-${step}`}
              question={questions[step]}
              questionIndex={step}
              totalQuestions={questions.length}
              selectedValue={answers[questions[step].id]}
              onSelect={handleSelect}
              onNext={handleNext}
              onPrev={handlePrev}
              config={config}
              feedbackMsg={feedbackMsg}
            />
          )}
          {phase === 'result' && (
            <ResultScreen
              key="result"
              result={getResult(totalScore)}
              totalScore={totalScore}
              maxScore={maxScore}
              config={config}
              onRegister={handleRegister}
              tokensAwarded={tokensAwarded}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
